import { useEffect, useState, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { useTourStore } from '@/hooks/useTour';
import { taskGuides } from './taskGuides';
import { X } from 'lucide-react';

export const TaskGuideOverlay = () => {
  const { taskGuideActive, taskGuideId, taskGuideStepIndex, nextTaskStep, endTaskGuide } = useTourStore();
  const [targetRect, setTargetRect] = useState<DOMRect | null>(null);
  const [showFallback, setShowFallback] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const overlayRef = useRef<HTMLDivElement>(null);

  const taskGuide = taskGuideId ? taskGuides[taskGuideId] : null;
  const currentStep = taskGuide?.steps[taskGuideStepIndex];
  const isLastStep = taskGuideStepIndex === (taskGuide?.steps.length || 0) - 1;

  // Reset fallback state when step changes
  useEffect(() => {
    setShowFallback(false);
    setTargetRect(null);
  }, [taskGuideStepIndex, taskGuideId]);

  useEffect(() => {
    if (!taskGuideActive || !currentStep) return;

    // Navigate to required route if specified
    if (currentStep.route && location.pathname !== currentStep.route) {
      navigate(currentStep.route);
      // Wait for navigation to complete before finding element
      setTimeout(() => findAndHighlightTarget(), 300);
    } else {
      findAndHighlightTarget();
    }
  }, [taskGuideActive, taskGuideStepIndex, currentStep, location.pathname]);

  const findAndHighlightTarget = () => {
    if (!currentStep?.targetId) {
      // No target specified - show as fullscreen/fallback immediately
      setShowFallback(true);
      return;
    }

    // Retry finding element with exponential backoff
    let attempts = 0;
    const maxAttempts = 8; // Reduced from 10 for faster fallback
    
    const tryFindElement = () => {
      const target = document.querySelector(`[data-tour-id="${currentStep.targetId}"]`);
      if (target) {
        const rect = target.getBoundingClientRect();
        setTargetRect(rect);
        setShowFallback(false);
        
        // Scroll target into view if needed
        target.scrollIntoView({ behavior: 'smooth', block: 'center' });
      } else if (attempts < maxAttempts) {
        attempts++;
        setTimeout(tryFindElement, 100 * Math.pow(1.5, attempts));
      } else {
        // Element not found after max attempts - show fallback instead of blank screen
        console.warn(`Could not find element with data-tour-id="${currentStep.targetId}", showing fallback`);
        setShowFallback(true);
      }
    };

    tryFindElement();
  };

  const handleNext = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (isLastStep) {
      endTaskGuide();
      if (currentStep?.cta?.route) {
        navigate(currentStep.cta.route);
      }
    } else {
      nextTaskStep();
    }
  };

  const handleClose = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    endTaskGuide();
  };

  if (!taskGuideActive || !currentStep) return null;

  const isFullscreen = currentStep.type === 'fullscreen' || !currentStep.targetId || showFallback;

  return (
    <div 
      ref={overlayRef}
      className="fixed inset-0 z-[9999]"
    >
      {/* Always-visible close button */}
      <button
        onClick={handleClose}
        className="absolute right-4 z-[10001] pointer-events-auto w-10 h-10 rounded-full bg-background/90 backdrop-blur-sm flex items-center justify-center shadow-lg border border-border/50 tap-scale"
        style={{ top: 'calc(1rem + var(--safe-area-top, 0px))' }}
        aria-label="Close guide"
      >
        <X className="w-5 h-5 text-foreground" />
      </button>

      {/* Backdrop with optional spotlight cutout */}
      {!isFullscreen && targetRect ? (
        <div 
          className="absolute inset-0 bg-black/40 transition-all duration-300 pointer-events-none"
          style={{
            clipPath: `polygon(
              0 0, 100% 0, 100% 100%, 0 100%, 0 0,
              ${targetRect.left - 8}px ${targetRect.top - 8}px,
              ${targetRect.left - 8}px ${targetRect.bottom + 8}px,
              ${targetRect.right + 8}px ${targetRect.bottom + 8}px,
              ${targetRect.right + 8}px ${targetRect.top - 8}px,
              ${targetRect.left - 8}px ${targetRect.top - 8}px
            )`
          }}
        />
      ) : (
        // Fullscreen/fallback - tap anywhere to dismiss
        <div 
          className="absolute inset-0 bg-black/40 pointer-events-auto cursor-pointer"
          onClick={handleClose}
        />
      )}

      {/* Tooltip */}
      <div 
        className="absolute pointer-events-auto bg-card rounded-xl px-4 py-3 card-shadow max-w-xs z-[10000]"
        style={!isFullscreen && targetRect ? getTooltipPosition(targetRect, currentStep.position) : {
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)'
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between gap-3">
          <p className="text-sm font-medium text-foreground">
            {currentStep.title}
          </p>
          <Button 
            onClick={handleNext}
            size="sm"
            className="shrink-0 h-8"
          >
            Got it
          </Button>
        </div>
        {showFallback && (
          <p className="text-xs text-muted-foreground mt-2">
            Tap outside to close
          </p>
        )}
      </div>
    </div>
  );
};

function getTooltipPosition(targetRect: DOMRect, position: string = 'bottom'): React.CSSProperties {
  const tooltipOffset = 16;
  const viewportWidth = window.innerWidth;
  const viewportHeight = window.innerHeight;

  let style: React.CSSProperties = {};

  switch (position) {
    case 'top':
      style = {
        left: Math.max(16, Math.min(targetRect.left + targetRect.width / 2, viewportWidth - 16)),
        top: targetRect.top - tooltipOffset,
        transform: 'translate(-50%, -100%)',
      };
      break;
    case 'bottom':
      style = {
        left: Math.max(16, Math.min(targetRect.left + targetRect.width / 2, viewportWidth - 16)),
        top: targetRect.bottom + tooltipOffset,
        transform: 'translateX(-50%)',
      };
      break;
    case 'left':
      style = {
        left: targetRect.left - tooltipOffset,
        top: targetRect.top + targetRect.height / 2,
        transform: 'translate(-100%, -50%)',
      };
      break;
    case 'right':
      style = {
        left: targetRect.right + tooltipOffset,
        top: targetRect.top + targetRect.height / 2,
        transform: 'translateY(-50%)',
      };
      break;
    default:
      style = {
        left: Math.max(16, Math.min(targetRect.left + targetRect.width / 2, viewportWidth - 16)),
        top: targetRect.bottom + tooltipOffset,
        transform: 'translateX(-50%)',
      };
  }

  // Ensure tooltip stays within viewport
  if (style.top && typeof style.top === 'number') {
    if (style.top < 16) style.top = 16;
    if (style.top > viewportHeight - 200) style.top = viewportHeight - 216;
  }

  return style;
}
